import { atom } from 'jotai';
import { atomWithReset } from 'jotai/utils';

import { oddsModelLeaguesAtom, oddsPageAtom } from './odds-page';
import { MaybeMatch } from '@nf/utils-common/fpw/monadic';
import { stringEmptyCheck } from '@nf/utils-common/fpw/utils';
import { leagueService } from '@/services/league.service';

type FilteredLeagueIdsSet = Record<number, Array<number>>;

type SelectedLeagues = Record<string, FilteredLeagueIdsSet>;

type WorldSelectedLeagues = {
	[key: string]: Record<string, FilteredLeagueIdsSet>;
};

type UpdateSelection = {
	leagueSet: Set<number>;
	mode?: string;
	market?: string;
	sport?: number;
}

// keys for localStorage
const PERSIST_SELECTED_LEAGUES = 'persist:selected-leagues';
const PERSIST_SELECTED_WORLD_LEAGUES = 'persist:selected-world-leagues';

export type LeagueFilterList = Array<{
	leagueId: number;
	leagueName: string;
	checked: boolean;
}>;

export const filterAtom = atom({
	market: '',
	sport: 0,
	mode: '',
});

export const oddsLeaguesForFilterAtom = atom(get => {
	const filter = get(filterAtom);
	const oddsModel = get(oddsPageAtom);
	const oddsLeagues = get(oddsModelLeaguesAtom);

	// Filter menuType when currently on the [world] page
	return leagueService.getFilteredOddsLeaguesByMode(oddsModel, oddsLeagues, filter.mode);
});

const getInitialSelectedLeagues = <T>(key: string): T => {
	if (typeof window === 'undefined') return {} as T;

	const selectedLeagues = localStorage.getItem(key);

	return (
		selectedLeagues 
			? (JSON.parse(selectedLeagues || 'null') ?? {})
			: {}
	) as T;
};

export const selectedLeaguesAtom = atom<SelectedLeagues>(
	getInitialSelectedLeagues<SelectedLeagues>(PERSIST_SELECTED_LEAGUES)
);

export const selectedWorldLeaguesAtom = atom<WorldSelectedLeagues>(
	getInitialSelectedLeagues<WorldSelectedLeagues>(PERSIST_SELECTED_WORLD_LEAGUES)
);

export const selectedLeaguesSetAtom = atom<Array<number> | undefined>(get => {
	const filter = get(filterAtom);
	const selectedLeagues = get(selectedLeaguesAtom);
	const selectedWorldLeagues = get(selectedWorldLeaguesAtom);

	return stringEmptyCheck(filter.mode)
		.fold(
			() => selectedLeagues[filter.market]?.[filter.sport],
			() => selectedWorldLeagues[filter.mode]?.[filter.market]?.[filter.sport]
		);
});

export const selectedLeaguesAmountAtom = atom(
	get => get(selectedLeaguesSetAtom)?.length ?? 0
);

export const keywordAtom = atomWithReset('');

export const isInitialOrEmptyAtom = atom(get => {
	const selectedLeaguesSet = get(selectedLeaguesSetAtom);

	return selectedLeaguesSet === undefined || selectedLeaguesSet.length === 0;
});

export const isSelectAllCheckedAtom = atom<boolean>(get => {
	const oddsLeagues = get(oddsLeaguesForFilterAtom);
	const selectedLeaguesAmount = get(selectedLeaguesAmountAtom);
	const isInitialOrEmpty = get(isInitialOrEmptyAtom);

	return MaybeMatch(isInitialOrEmpty)
		.fold(
			() => selectedLeaguesAmount === oddsLeagues.size,
			() => true
		);
});

export const isSelectedOnlyAtom = atom(false);

export const filteredResultAtom = atom<LeagueFilterList>(get => {
	const keyword = get(keywordAtom);
	const oddsLeagues = get(oddsLeaguesForFilterAtom);
	const selectedLeaguesSet = get(selectedLeaguesSetAtom);
	const isInitialOrEmpty = get(isInitialOrEmptyAtom);

	// Example of the league name: "*NBA - Most Points Scored|l_56049.png"
	const result = [...oddsLeagues]
		.filter(([leagueId, leagueName]) => leagueName.includes(keyword))
		.map(([leagueId, leagueName]) => ({
			leagueId,
			leagueName: leagueName.split('|')[0],
			checked: isInitialOrEmpty || Boolean(selectedLeaguesSet?.includes(leagueId))
		}))
		.sort((a, b) => a.leagueName.localeCompare(b.leagueName));
	
	return result;
});

export const saveLeagueSelectionAtom = atom(null, (get, set, updateSelection: UpdateSelection) => {
	const filter = get(filterAtom);
	const sportLeagueSet = [...updateSelection.leagueSet];

	const filterMode = updateSelection.mode ?? filter.mode;
	const filterMarket = updateSelection.market ?? filter.market;
	const filterSport = updateSelection.sport ?? filter.sport;
	
	stringEmptyCheck(filterMode)
		.fork({
			just: () => {
				set(selectedWorldLeaguesAtom, leaguesModel => {
					const modeMarkets = leaguesModel[filterMode] ?? {};
					const marketLeagues = modeMarkets[filterMarket] ?? {};

					marketLeagues[filterSport] = sportLeagueSet;
					modeMarkets[filterMarket] = marketLeagues;
					leaguesModel[filterMode] = modeMarkets;
					localStorage.setItem(PERSIST_SELECTED_WORLD_LEAGUES, JSON.stringify(leaguesModel));

					return { ...leaguesModel };
				})
			},
			none: () => {
				set(selectedLeaguesAtom, leaguesModel => {
					const marketLeagues = leaguesModel[filterMarket] ?? {};
						
					marketLeagues[filterSport] = sportLeagueSet;
					leaguesModel[filterMarket] = marketLeagues;
					localStorage.setItem(PERSIST_SELECTED_LEAGUES, JSON.stringify(leaguesModel));
			
					return { ...leaguesModel };
				});
			}
		});
});